/* Photo cycle for product cards whose .card__media carries data-azan-cycle
   (the clothing cards, see snippets/card-product.liquid). While the mouse is
   over the card, or a finger holds it (long press on phones and tablets), the
   product's photos fade through one after another and back to the first;
   leaving or lifting the finger fades back to the first photo. Photos load
   only then, one ahead, at the card's rendered width. Delegated from document,
   so cards that facets, recommendations or sliders render later work too. */
(() => {
  const FIRST = 350; /* hover delay before the first change */
  const HOLD = 400; /* long-press time on touch */
  const STEP = 1000; /* time per photo */
  const FADE = 450; /* matches the opacity transition in component-card.css */
  const MOVE = 10; /* px a finger may drift before the hold counts as a scroll */
  const WIDTHS = [360, 533, 720, 940, 1066, 1500];

  let current = null;
  let press = null;
  let swallowClickUntil = 0;

  const cardOf = (target) => {
    const card = target && target.closest ? target.closest('.card-wrapper') : null;
    return card && card.querySelector('.card__media[data-azan-cycle]') ? card : null;
  };

  const layer = (media) => {
    if (media.azanCycle) return media.azanCycle;
    const need = media.clientWidth * (window.devicePixelRatio || 1);
    const width = WIDTHS.find((w) => w >= need) || WIDTHS[WIDTHS.length - 1];
    const box = document.createElement('div');
    box.className = 'azan-cycle';
    box.setAttribute('aria-hidden', 'true');
    const imgs = media
      .getAttribute('data-azan-cycle')
      .split('|')
      .filter(Boolean)
      .map((url) => {
        const img = document.createElement('img');
        img.alt = '';
        img.decoding = 'async';
        img.draggable = false;
        img.setAttribute('data-azan-no-trim', '');
        img.azanSrc = `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
        box.appendChild(img);
        return img;
      });
    media.appendChild(box);
    media.azanCycle = { imgs, at: -1, on: null, z: 1, timer: 0, gen: 0, card: null };
    return media.azanCycle;
  };

  const load = (img) => {
    if (!img.azanReady) {
      img.azanReady = new Promise((resolve) => {
        img.onload = () => {
          const done = () => resolve();
          if (img.decode) img.decode().then(done, done);
          else done();
        };
        img.onerror = () => {
          img.azanFailed = true;
          resolve();
        };
        img.src = img.azanSrc;
      });
    }
    return img.azanReady;
  };

  /* The incoming photo goes on top and fades in; the outgoing one leaves only
     after the fade, so the first photo never flashes through in between.
     null fades back to the card's own first photo underneath. */
  const show = (state, img) => {
    const prev = state.on;
    state.on = img;
    if (img) {
      state.z += 1;
      img.style.zIndex = state.z;
      img.classList.add('is-on');
    }
    if (!prev || prev === img) return;
    if (img) {
      setTimeout(() => {
        if (state.on !== prev) prev.classList.remove('is-on');
      }, FADE);
    } else {
      prev.classList.remove('is-on');
    }
  };

  const step = (state, gen) => {
    if (state.gen !== gen) return;
    state.at = state.at + 1 < state.imgs.length ? state.at + 1 : -1;
    const img = state.at >= 0 ? state.imgs[state.at] : null;
    (img ? load(img) : Promise.resolve()).then(() => {
      if (state.gen !== gen) return;
      if (img && img.azanFailed) {
        step(state, gen);
        return;
      }
      show(state, img);
      load(state.imgs[state.at + 1 < state.imgs.length ? state.at + 1 : 0]);
      state.timer = setTimeout(() => step(state, gen), STEP);
    });
  };

  function onLeave(event) {
    if (event.pointerType !== 'touch') stop();
  }

  function stop() {
    const state = current;
    if (!state) return;
    current = null;
    state.gen += 1;
    clearTimeout(state.timer);
    state.at = -1;
    show(state, null);
    state.card.removeEventListener('pointerleave', onLeave);
  }

  const start = (card, delay) => {
    const state = layer(card.querySelector('.card__media[data-azan-cycle]'));
    if (!state.imgs.length) return;
    stop();
    current = state;
    state.card = card;
    state.gen += 1;
    const gen = state.gen;
    load(state.imgs[0]);
    state.timer = setTimeout(() => step(state, gen), delay);
    card.addEventListener('pointerleave', onLeave);
  };

  /* Mouse and pen: hover. */
  document.addEventListener(
    'pointerover',
    (event) => {
      if (event.pointerType === 'touch') return;
      const card = cardOf(event.target);
      if (card && !(current && current.card === card)) start(card, FIRST);
    },
    { passive: true }
  );

  /* Touch: long press. Moving the finger first (a scroll or a slider swipe)
     cancels it; the click right after a long press is swallowed so lifting the
     finger does not open the product. */
  const endPress = () => {
    if (!press) return;
    clearTimeout(press.timer);
    if (press.active) {
      stop();
      swallowClickUntil = Date.now() + 800;
    }
    press = null;
  };

  document.addEventListener(
    'pointerdown',
    (event) => {
      if (event.pointerType !== 'touch' || !event.isPrimary) return;
      endPress();
      const card = cardOf(event.target);
      if (!card) return;
      const hold = { card, id: event.pointerId, x: event.clientX, y: event.clientY, active: false, timer: 0 };
      hold.timer = setTimeout(() => {
        hold.active = true;
        start(card, 0);
      }, HOLD);
      press = hold;
    },
    { passive: true }
  );

  document.addEventListener(
    'pointermove',
    (event) => {
      if (!press || press.active || event.pointerId !== press.id) return;
      if (Math.abs(event.clientX - press.x) > MOVE || Math.abs(event.clientY - press.y) > MOVE) endPress();
    },
    { passive: true }
  );

  ['pointerup', 'pointercancel'].forEach((type) => {
    document.addEventListener(
      type,
      (event) => {
        if (press && event.pointerId === press.id) endPress();
      },
      { passive: true }
    );
  });

  document.addEventListener(
    'click',
    (event) => {
      if (Date.now() > swallowClickUntil || !cardOf(event.target)) return;
      swallowClickUntil = 0;
      event.preventDefault();
      event.stopPropagation();
    },
    true
  );

  /* Android opens the link menu on a long press; on these cards the hold is ours. */
  document.addEventListener(
    'contextmenu',
    (event) => {
      if (press && cardOf(event.target)) event.preventDefault();
    },
    true
  );

  window.addEventListener('pagehide', () => {
    endPress();
    stop();
  });
})();
